import type { ShareLinkPlatformId } from '@/lib/socialShareUrls';

export type ShareChannel = ShareLinkPlatformId | 'copy_link';

export type BlogShareInteractionPayload = {
  action: 'share_click' | 'copy_link';
  platform: ShareChannel;
  /** Blog post slug */
  slug: string;
  /** Canonical article URL */
  url: string;
};

type WindowWithOptionalAnalytics = Window &
  Partial<{
    dataLayer: Record<string, unknown>[];
    plausible: (event: string, options?: { props?: Record<string, unknown> }) => void;
    posthog: { capture: (event: string, props?: Record<string, unknown>) => void };
    gtag: (...args: unknown[]) => void;
  }>;

/**
 * Provider-agnostic hook: CustomEvent + optional common analytics globals.
 * Also invoke `onShareInteraction` from the component when passed.
 */
export function dispatchBlogShareInteraction(payload: BlogShareInteractionPayload): void {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent<BlogShareInteractionPayload>('figtures:blog-share', { detail: payload }));

  const w = window as WindowWithOptionalAnalytics;

  if (Array.isArray(w.dataLayer)) {
    w.dataLayer.push({
      event: 'blog_share',
      share_action: payload.action,
      share_platform: payload.platform,
      blog_slug: payload.slug,
      page_url: payload.url,
    });
  }

  if (typeof w.plausible === 'function') {
    w.plausible('Blog Share', {
      props: {
        action: payload.action,
        platform: payload.platform,
        slug: payload.slug,
      },
    });
  }

  if (w.posthog && typeof w.posthog.capture === 'function') {
    w.posthog.capture('blog_share', {
      action: payload.action,
      platform: payload.platform,
      slug: payload.slug,
      url: payload.url,
    });
  }

  if (typeof w.gtag === 'function') {
    w.gtag('event', 'share', {
      method: payload.platform,
      content_type: 'blog_post',
      item_id: payload.slug,
    });
  }
}
